import { Check, MapPin, Network, ShieldCheck, ShieldOff, Tornado, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { PageHeader } from '@/components/common/page-header';
import { SectionCard } from '@/components/common/section-card';
import { StatTile } from '@/components/common/stat-tile';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DeployThinExitDialog } from '@/components/fleet/deploy-thin-exit-dialog';
import { ErrorState, TableSkeleton } from '@/components/fleet/states';
import { useCreateServer } from '@/hooks/use-create-server';
import { useFleetServers } from '@/hooks/use-fleet-servers';
import { ApiError } from '@/lib/api-client';
import { computeAudienceCoverage } from '@/lib/audience-tiers';
import {
  computeExitCoverage,
  PLANNED_EXITS,
  TARGET_NODES_BY_TIER,
  type ExitCoverageEntry,
  type PlannedExit,
  type PurposeTier,
} from '@/lib/planned-regions';
import { cn } from '@/lib/utils';
import type { ServerListItem } from '@/types/fleet';
import type { VpnProtocol } from '@/types/provision';

const PROVIDERS = [
  'hetzner', 'ovh', 'vultr', 'buyvm', 'hosthatch', 'flokinet',
  'privex', 'aws', 'azure', 'gcp', 'other',
] as const;

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm';

/**
 * Protocols an exit may be registered with. WireGuard and OpenVPN are
 * intentionally not offered — the platform serves neither (see VpnProtocol),
 * and the fleet API rejects both with a 400.
 */
const PROTOCOL_OPTIONS: { label: string; value: VpnProtocol }[] = [
  { label: 'AmneziaWG', value: 'amneziawg' },
  { label: 'Tor', value: 'tor' },
  { label: 'Mixnet', value: 'mixnet' },
];

/**
 * Protocols a freshly-registered exit should serve, per tier. Written to
 * `server_protocols` on create so the routing selector can pick the box — and
 * so provisioning configures the right protocol. Operators can adjust before
 * registering.
 *
 * AmneziaWG is the platform's only client tunnel protocol; a general/p2p exit
 * registered with anything else is unroutable.
 */
const DEFAULT_PROTOCOLS_BY_TIER: Record<PurposeTier, VpnProtocol[]> = {
  general: ['amneziawg'],
  p2p: ['amneziawg'],
  tor: ['tor'],
  mixnet: ['mixnet'],
};

const TIER_META: Record<PurposeTier, { title: string; icon: typeof MapPin; blurb: string }> = {
  general: {
    title: 'General VPN exits',
    icon: MapPin,
    blurb:
      'One per region. Serves the bulk of user traffic over AmneziaWG.',
  },
  p2p: {
    title: 'P2P exits',
    icon: Network,
    blurb:
      'DMCA-friendly jurisdictions only. Users explicitly opt-in via P2P mode; selector filters on the `p2p` capability.',
  },
  tor: {
    title: 'Tor exits',
    icon: Tornado,
    blurb:
      'Tor-tolerant jurisdictions only. Capability `tor` must be set after provisioning (configure-tor playbook).',
  },
  mixnet: {
    title: 'Mixnet (Nym)',
    icon: ShieldOff,
    blurb:
      'Nym gateways in privacy-friendly jurisdictions. Capability `mixnet` is wired into the routing selector — a registered + provisioned box is selectable at session-mint time.',
  },
};

/** Inline registration form for a planned region. Registers one OR several
 *  nodes at once (mixnet regions target 3) — provider / SSH user / protocols are
 *  shared, each node carries its own IP + SSH password, names auto-increment. */
function RegisterExitForm({
  plan,
  servers,
  onAdded,
}: {
  plan: PlannedExit;
  servers: ServerListItem[];
  onAdded: () => void;
}): JSX.Element {
  const existingInRegion = servers.filter(
    (s) => s.region?.country?.toUpperCase() === plan.country.toUpperCase(),
  );
  // Pre-fill enough rows to reach the tier target (mixnet → 3), at least one.
  const initialRows = Math.max(1, TARGET_NODES_BY_TIER[plan.purpose] - existingInRegion.length);

  const [provider, setProvider] = useState<string>(plan.providerSlug);
  const [sshUsername, setSshUsername] = useState('root');
  const [protocols, setProtocols] = useState<VpnProtocol[]>(
    () => DEFAULT_PROTOCOLS_BY_TIER[plan.purpose],
  );
  const [provisionNow, setProvisionNow] = useState(false);
  const [nodes, setNodes] = useState<{ ip: string; password: string }[]>(
    () => Array.from({ length: initialRows }, () => ({ ip: '', password: '' })),
  );
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const create = useCreateServer();

  /** Auto name for the i-th new node, continuing the region's numbering. */
  function nodeName(i: number): string {
    return plan.nameTemplate.replace(
      '{N}',
      String(existingInRegion.length + i + 1).padStart(2, '0'),
    );
  }
  function updateNode(i: number, patch: Partial<{ ip: string; password: string }>): void {
    setNodes((cur) => cur.map((n, j) => (j === i ? { ...n, ...patch } : n)));
  }
  function addNode(): void {
    setNodes((cur) => [...cur, { ip: '', password: '' }]);
  }
  function removeNode(i: number): void {
    setNodes((cur) => (cur.length > 1 ? cur.filter((_, j) => j !== i) : cur));
  }
  function toggleProtocol(value: VpnProtocol): void {
    setProtocols((cur) =>
      cur.includes(value) ? cur.filter((p) => p !== value) : [...cur, value],
    );
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    if (!sshUsername.trim()) return setError('SSH username is required.');
    if (protocols.length === 0) {
      return setError('Select at least one protocol these servers will serve.');
    }
    const rows = nodes.map((n) => ({ ip: n.ip.trim(), password: n.password }));
    if (rows.some((n) => !n.ip)) return setError('Every node needs a public IP.');
    if (rows.some((n) => !n.password)) {
      return setError('Every node needs an SSH password so the playbook can reach it.');
    }

    setSubmitting(true);
    let ok = 0;
    try {
      for (let i = 0; i < rows.length; i++) {
        await create.mutateAsync({
          name: nodeName(i),
          role: 'vpn_server',
          public_ip: rows[i].ip,
          provider: provider.trim(),
          region_continent: plan.continent,
          region_country: plan.country,
          region_city: plan.cityDefault,
          ssh_username: sshUsername.trim(),
          ssh_password: rows[i].password,
          protocols: protocols.map((protocol) => ({ protocol })),
          // Set the tier's capability (tor/mixnet/p2p) at registration so the
          // box is routing-selectable and coverage actually counts it. Without
          // this, a registered tor/mixnet exit never matches its plan.
          capabilities: plan.requiredCapability ? [plan.requiredCapability] : undefined,
          auto_provision: provisionNow,
        });
        ok += 1;
      }
      toast.success(
        `Registered ${ok} server${ok === 1 ? '' : 's'} for ${plan.label}.` +
          (provisionNow
            ? ' Provisioning started — track each on its detail page.'
            : ' Run preflight + provision from each detail page.'),
      );
      setNodes([{ ip: '', password: '' }]);
      onAdded();
    } catch (err) {
      setError(
        (err instanceof ApiError ? err.message : 'Could not register the servers.') +
          (ok > 0 ? ` (${ok} of ${rows.length} registered before the error)` : ''),
      );
      if (ok > 0) onAdded();
    } finally {
      setSubmitting(false);
    }
  }

  const submitLabel = submitting
    ? `Registering ${nodes.length}…`
    : nodes.length > 1
      ? `Register ${nodes.length} servers`
      : provisionNow
        ? 'Register & provision'
        : 'Register server';

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-3" autoComplete="off">
      {/* Shared across every node in this batch */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor={`prov-${plan.id}`}>Provider</Label>
          <select id={`prov-${plan.id}`} className={SELECT_CLASS} value={provider} onChange={(e) => setProvider(e.target.value)}>
            {PROVIDERS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <Label htmlFor={`user-${plan.id}`}>SSH username</Label>
          <Input id={`user-${plan.id}`} required value={sshUsername} onChange={(e) => setSshUsername(e.target.value)} placeholder="root" autoComplete="off" />
        </div>
      </div>

      {/* One row per node — each box has its own IP + SSH password */}
      <div className="space-y-2">
        <Label>Nodes to register ({nodes.length})</Label>
        {nodes.map((node, i) => (
          <div key={i} className="grid grid-cols-1 items-end gap-2 rounded-md border border-border p-2 sm:grid-cols-[110px_1fr_1fr_auto]">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">Name</span>
              <p className="font-mono text-sm">{nodeName(i)}</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor={`ip-${plan.id}-${i}`}>{i === 0 ? 'Public IP' : `Public IP ${i + 1}`}</Label>
              <Input id={`ip-${plan.id}-${i}`} required value={node.ip} onChange={(e) => updateNode(i, { ip: e.target.value })} placeholder="203.0.113.10" />
            </div>
            <div className="space-y-1">
              <Label htmlFor={`pw-${plan.id}-${i}`}>{i === 0 ? 'SSH password' : `SSH password ${i + 1}`}</Label>
              <Input id={`pw-${plan.id}-${i}`} required type="password" value={node.password} onChange={(e) => updateNode(i, { password: e.target.value })} placeholder="encrypted server-side" autoComplete="new-password" />
            </div>
            {nodes.length > 1 ? (
              <Button type="button" size="sm" variant="ghost" className="h-9 text-muted-foreground" onClick={() => removeNode(i)}>
                Remove
              </Button>
            ) : (
              <span />
            )}
          </div>
        ))}
        <Button type="button" size="sm" variant="outline" onClick={addNode}>
          <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
          Add another node
        </Button>
      </div>

      <div className="space-y-1.5">
        <Label>Protocols served</Label>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {PROTOCOL_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                className="h-3.5 w-3.5 rounded border-input"
                checked={protocols.includes(opt.value)}
                onChange={() => toggleProtocol(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Recorded on each server so the router can select it; the first one is what
          provisioning configures. Defaulted to the {plan.purpose} tier.
        </p>
      </div>

      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">
          Region pre-filled: <span className="font-mono">{plan.continent}</span> · <span className="font-mono">{plan.country}</span> · <span className="font-mono">{plan.cityDefault}</span>
          {plan.requiredCapability && (
            <> · capability <span className="font-mono">{plan.requiredCapability}</span> set during provisioning</>
          )}
        </p>
        <div className="flex items-center gap-3">
          <label
            htmlFor={`provnow-${plan.id}`}
            className="flex items-center gap-1.5 text-xs text-muted-foreground"
            title="Kick off the provisioning playbook immediately after registering, instead of running preflight + provision manually from the detail page."
          >
            <input
              id={`provnow-${plan.id}`}
              type="checkbox"
              className="h-3.5 w-3.5 rounded border-input"
              checked={provisionNow}
              onChange={(e) => setProvisionNow(e.target.checked)}
            />
            Provision immediately
          </label>
          <Button type="submit" size="sm" disabled={submitting}>
            {submitLabel}
          </Button>
        </div>
      </div>

      {error && (
        <p className="text-sm text-destructive" role="alert">{error}</p>
      )}
    </form>
  );
}

function ExitRow({
  entry,
  allServers,
  onAdded,
}: {
  entry: ExitCoverageEntry;
  allServers: ServerListItem[];
  onAdded: () => void;
}): JSX.Element {
  const { plan, matches, target, covered } = entry;
  const started = matches.length > 0;
  const [expanded, setExpanded] = useState(false);

  return (
    <li className={'border-b border-border last:border-b-0 ' + (covered ? 'bg-status-active/[0.05]' : '')}>
      <div className="flex items-center gap-3 py-3">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full">
          {covered
            ? <Check className="h-4 w-4 text-status-active" aria-hidden />
            : <span className="h-3 w-3 rounded-full border-2 border-status-degraded" aria-hidden />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">
            {plan.label}
            {target > 1 && (
              <span
                className={
                  'ml-1.5 font-mono text-xs font-normal ' +
                  (covered ? 'text-status-active' : 'text-status-degraded')
                }
              >
                {matches.length}/{target} nodes
              </span>
            )}
            {target === 1 && covered && matches.length > 1 && (
              <span className="text-xs text-muted-foreground"> · {matches.length} servers</span>
            )}
          </p>
          {started ? (
            <p className="font-mono text-xs text-muted-foreground">
              {matches.map((s, i) => (
                <span key={s.id}>
                  {i > 0 && ', '}
                  <Link to={`/fleet/${encodeURIComponent(s.name)}`} className="hover:text-foreground hover:underline">{s.name}</Link>
                </span>
              ))}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground">Suggested: {plan.providerHint}</p>
          )}
        </div>
        <Badge variant="outline" className="shrink-0">{plan.country}</Badge>
        {expanded
          ? <Button size="sm" variant="ghost" onClick={() => setExpanded(false)}>Cancel</Button>
          : (
            <Button size="sm" variant={covered ? 'outline' : 'default'} onClick={() => setExpanded(true)}>
              <Plus className="mr-1 h-3.5 w-3.5" aria-hidden />
              {started ? 'Add another' : 'Add IP'}
            </Button>
          )
        }
      </div>
      {expanded && (
        <div className="mb-3 ml-9 rounded-md border border-border bg-secondary/30 p-3">
          <RegisterExitForm plan={plan} servers={allServers} onAdded={onAdded} />
        </div>
      )}
    </li>
  );
}

function TierSection({
  tier,
  entries,
  allServers,
  onAdded,
}: {
  tier: PurposeTier;
  entries: ExitCoverageEntry[];
  allServers: ServerListItem[];
  onAdded: () => void;
}): JSX.Element {
  const meta = TIER_META[tier];
  const Icon = meta.icon;
  const covered = entries.filter((e) => e.covered).length;
  const total = entries.length;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Icon className="h-4 w-4 text-muted-foreground" aria-hidden />
          {meta.title}
          <Badge variant={covered === total ? 'active' : 'degraded'} className="ml-1">
            {covered} / {total}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground">{meta.blurb}</p>
      </CardHeader>
      <CardContent>
        <ul className="divide-y divide-border">
          {entries.map((entry) => (
            <ExitRow key={entry.plan.id} entry={entry} allServers={allServers} onAdded={onAdded} />
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * Audience grouping by censorship tier. High-censorship audiences (IR/CN/RU)
 * are auto-routed to stealth/VLESS-Reality servers by the backend, so this
 * panel surfaces whether the fleet currently has stealth coverage for them.
 */
function AudienceSection({ servers }: { servers: ServerListItem[] }): JSX.Element {
  const { stealthServers, audiences, covered } = computeAudienceCoverage(servers);

  return (
    <SectionCard
      title={
        <span className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-status-active" />
          Audience · High-censorship (Reality)
        </span>
      }
      description="Users in Iran, China & Russia are auto-routed to stealth (VLESS-Reality) servers. An audience is covered once the fleet has at least one stealth-capable server — the Reality edge pool sits outside these countries."
      action={
        <Badge variant={covered ? 'active' : 'failed'}>
          {stealthServers.length} stealth {stealthServers.length === 1 ? 'server' : 'servers'}
        </Badge>
      }
    >
      <div className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {audiences.map((audience) => (
            <span
              key={audience.code}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-sm',
                audience.covered
                  ? 'border-status-active/30 bg-status-active/10 text-status-active'
                  : 'border-status-degraded/30 bg-status-degraded/10 text-status-degraded',
              )}
            >
              {audience.covered ? (
                <Check className="h-3.5 w-3.5" />
              ) : (
                <ShieldOff className="h-3.5 w-3.5" />
              )}
              {audience.name}
              <span className="font-mono text-xs opacity-70">({audience.code})</span>
            </span>
          ))}
        </div>

        {stealthServers.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No stealth-capable servers yet — high-censorship users fall back to obfuscated
            protocols. Deploy a VLESS-Reality edge to close this gap.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-md border border-border text-sm">
            {stealthServers.map((server) => (
              <li key={server.id} className="flex items-center justify-between gap-3 px-3 py-2">
                <span className="flex items-center gap-2 font-mono">
                  <Network className="h-3.5 w-3.5 text-muted-foreground" />
                  {server.name}
                </span>
                <span className="font-mono text-muted-foreground">
                  {server.region?.city || server.region?.country || '—'}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </SectionCard>
  );
}

export function FleetRegionsPage(): JSX.Element {
  const serversQuery = useFleetServers({ page: 1, page_size: 200, sort: 'name' });
  const servers = serversQuery.data?.items ?? [];
  const coverage = computeExitCoverage(servers);

  // Group by tier in the order: general → p2p → tor → mixnet
  const byTier: Record<PurposeTier, ExitCoverageEntry[]> = {
    general: [], p2p: [], tor: [], mixnet: [],
  };
  for (const entry of coverage) byTier[entry.plan.purpose].push(entry);

  const totalPlanned = PLANNED_EXITS.length;
  const totalCovered = coverage.filter((c) => c.covered).length;
  const totalMissing = totalPlanned - totalCovered;

  function onAdded(): void {
    void serversQuery.refetch();
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Region coverage"
        description={
          <>
            Launch buildout against every tier in{' '}
            <code className="font-mono text-xs text-foreground">docs/runbooks/launch-fleet-procurement.md</code>{' '}
            — general VPN, P2P, Tor, Mixnet. Register inline as boxes are ordered.
          </>
        }
        actions={<DeployThinExitDialog />}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <StatTile
          label="Covered"
          value={
            <span className="text-status-active">
              {totalCovered} / {totalPlanned}
            </span>
          }
        />
        <StatTile
          label="Missing"
          value={<span className="text-status-degraded">{totalMissing}</span>}
        />
        <StatTile label="Progress" value={`${Math.round((totalCovered / totalPlanned) * 100)}%`} />
      </div>

      {serversQuery.isLoading && <TableSkeleton rows={8} />}
      {serversQuery.isError && (
        <ErrorState
          title="Couldn't load fleet."
          error={serversQuery.error}
          onRetry={() => void serversQuery.refetch()}
        />
      )}

      {serversQuery.data && (
        <div className="space-y-4">
          <AudienceSection servers={servers} />
          <TierSection tier="general" entries={byTier.general} allServers={servers} onAdded={onAdded} />
          <TierSection tier="p2p"     entries={byTier.p2p}     allServers={servers} onAdded={onAdded} />
          <TierSection tier="tor"     entries={byTier.tor}     allServers={servers} onAdded={onAdded} />
          <TierSection tier="mixnet"  entries={byTier.mixnet}  allServers={servers} onAdded={onAdded} />
        </div>
      )}
    </div>
  );
}
